import { Badge, Card, CardContent, Input, Spinner } from '@crm/ui';
import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Pagination } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { formatDateTime } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { useAudit } from '../api';

const METHOD_TONES: Record<string, string> = {
  POST: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  PATCH: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  PUT: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  DELETE: 'bg-destructive/15 text-destructive',
};

/** Auditoria: o que cada usuario alterou, na ordem em que aconteceu. */
export function AuditPage() {
  useDocumentTitle('Auditoria');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [entity, setEntity] = useState('');

  const audit = useAudit(page, entity || undefined, perPage);
  const meta = audit.data?.meta;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ScrollText />}
        title="Auditoria"
        description="Registros e alteracoes feitas pelos usuarios."
      />

      {audit.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(audit.error)}</p>
      ) : null}

      <Card variant="flush">
        {/* Filtro e lista no mesmo quadro; a lista segue sem recuo proprio. */}
        <CardContent className="pb-6">
          <Input
            className="max-w-xs"
            placeholder="Filtrar por modulo (ex.: appointments)"
            value={entity}
            onChange={(event) => {
              setEntity(event.target.value);
              setPage(1);
            }}
          />
        </CardContent>
        <CardContent className="p-0">
          {audit.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* No celular cada registro e um cartao: quem fez, o que fez e quando. */}
              <ul className="divide-y divide-border md:hidden">
                {(audit.data?.data.length ?? 0) === 0 ? (
                  <li className="px-4 py-10 text-center text-muted-foreground">
                    Nenhuma alteracao registrada.
                  </li>
                ) : (
                  audit.data?.data.map((entry) => (
                    <li key={entry.id} className="space-y-2 px-4 py-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="secondary"
                          className={METHOD_TONES[entry.method] ?? undefined}
                        >
                          {entry.method}
                        </Badge>
                        <span className="font-medium">{entry.entity}</span>
                      </div>
                      <p className="break-all text-sm text-muted-foreground">{entry.route}</p>
                      <p className="text-sm">
                        {entry.userName} - {formatDateTime(entry.createdAt)}
                      </p>
                      {entry.entityId ? (
                        <p className="break-all font-mono text-xs text-muted-foreground">
                          {entry.entityId}
                        </p>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Quando</th>
                      <th className="px-4 py-3 font-medium">Usuario</th>
                      <th className="px-4 py-3 font-medium">Acao</th>
                      <th className="px-4 py-3 font-medium">Modulo</th>
                      <th className="px-4 py-3 font-medium">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(audit.data?.data.length ?? 0) === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                          Nenhuma alteracao registrada.
                        </td>
                      </tr>
                    ) : (
                      audit.data?.data.map((entry) => (
                        <tr key={entry.id} className="border-t border-border">
                          <td className="whitespace-nowrap px-4 py-3">
                            {formatDateTime(entry.createdAt)}
                          </td>
                          <td className="px-4 py-3">{entry.userName}</td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-2">
                              <Badge
                                variant="secondary"
                                className={METHOD_TONES[entry.method] ?? undefined}
                              >
                                {entry.method}
                              </Badge>
                              <span className="text-muted-foreground">{entry.route}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">{entry.entity}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {entry.entityId ?? '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Pagination
        meta={meta}
        label="registros"
        onChange={setPage}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value);
          setPage(1);
        }}
      />
    </div>
  );
}
